import { useTranslation } from "react-i18next"

import type { OptionItem } from "../schemas/column-schema"
import type { TFunction } from "i18next"

import { Plus, Trash2, WandSparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import { OptionsBuilder } from "./options-builder"
import { useAddColumns } from "../hooks/use-add-columns"
import { useGetAiColumns } from "../hooks/use-get-ai-columns-mutation"
import { DATA_TYPES, DEFAULT_OPTION_COLORS, getDataTypeLabel } from "../schemas/column-schema"

type AddColumnsDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  hasFiles: boolean
}

export function AddColumnsDialog({ isOpen, onOpenChange, hasFiles }: AddColumnsDialogProps) {
  const { t } = useTranslation()
  const { form, addColumn, deleteColumnFromForm, addColumns, addColumnsMutation } = useAddColumns({
    isOpen,
    onSuccess: () => {
      onOpenChange(false)
    },
  })

  const columns = form.watch("columns")

  const getAiColumnsMutation = useGetAiColumns({
    onSuccess: (aiColumns) => {
      const isFirstColumnEmpty = columns[0].name === "" && columns[0].description === ""
      const isDefaultState = isFirstColumnEmpty && columns.length === 1

      if (isDefaultState) {
        form.setValue("columns", aiColumns)
      } else {
        form.setValue("columns", [...columns, ...aiColumns])
      }
    },
  })

  function AddAiColumnsButton() {
    const button = (
      <Button
        disabled={getAiColumnsMutation.isPending || addColumnsMutation.isPending || !hasFiles}
        type="button"
        variant="outline"
        onClick={() => getAiColumnsMutation.mutate()}
      >
        {getAiColumnsMutation.isPending ? <Spinner /> : <WandSparkles className="size-4" />}
        {t("pages.ai_parser.add_columns_dialog.add_ai_columns")}
      </Button>
    )

    if (!hasFiles) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent>
              <p>{t("pages.ai_parser.add_columns_dialog.ai_columns_tooltip")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return button
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("pages.ai_parser.add_columns_dialog.title")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            onSubmit={form.handleSubmit(addColumns)}
          >
            <ScrollArea className="flex-1">
              <div className="flex flex-col gap-4">
                {columns.map((_, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-sm font-medium">
                          {t("pages.ai_parser.add_columns_dialog.column")} {index + 1}
                        </CardTitle>
                        <Button
                          className="h-8 w-8 p-0"
                          disabled={columns.length === 1}
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={() => deleteColumnFromForm(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">
                            {t("pages.ai_parser.add_columns_dialog.remove_column")}
                          </span>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control}
                        name={`columns.${index}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("pages.ai_parser.add_columns_dialog.field_title")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full"
                                placeholder={t(
                                  "pages.ai_parser.add_columns_dialog.title_placeholder"
                                )}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`columns.${index}.dataType`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("pages.ai_parser.add_columns_dialog.field_type")}
                            </FormLabel>
                            <Select
                              defaultValue={field.value}
                              onValueChange={(value) => {
                                field.onChange(value)
                                if (value === "options") {
                                  const currentOptions =
                                    form.getValues(`columns.${index}.options`) ?? []
                                  if (currentOptions.length === 0) {
                                    form.setValue(`columns.${index}.options`, [
                                      {
                                        id: crypto.randomUUID(),
                                        label: "",
                                        color: DEFAULT_OPTION_COLORS[0],
                                      },
                                    ])
                                  }
                                }
                              }}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "pages.ai_parser.add_columns_dialog.select_data_type"
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {DATA_TYPES.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    <span className="font-medium">{getDataTypeLabel(type, t)}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormDescription>
                              {getDataTypeDescription(field.value, t)}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {columns[index].dataType === "options" && (
                        <div className="bg-muted/30 rounded-lg border p-4">
                          <OptionsBuilder
                            options={form.watch(`columns.${index}.options`) ?? []}
                            optionTypeId={form.watch(`columns.${index}.optionTypeId`)}
                            onOptionsChange={(options: OptionItem[]) =>
                              form.setValue(`columns.${index}.options`, options)
                            }
                            onOptionTypeIdChange={(optionTypeId) =>
                              form.setValue(`columns.${index}.optionTypeId`, optionTypeId)
                            }
                          />
                        </div>
                      )}

                      <FormField
                        control={form.control}
                        name={`columns.${index}.description`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("pages.ai_parser.add_columns_dialog.description")}
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                className="min-h-[100px] resize-none"
                                placeholder={t(
                                  "pages.ai_parser.add_columns_dialog.description_placeholder"
                                )}
                              />
                            </FormControl>
                            <FormDescription>
                              {t("pages.ai_parser.add_columns_dialog.description_help")}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>

            <DialogFooter className="flex w-full items-center pt-6 sm:justify-between">
              <div className="flex items-center gap-2">
                {AddAiColumnsButton()}
                <Button
                  disabled={addColumnsMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={addColumn}
                >
                  <Plus className="h-4 w-4" />
                  {t("pages.ai_parser.add_columns_dialog.add_column")}
                </Button>
              </div>
              <Button disabled={addColumnsMutation.isPending} type="submit">
                {addColumnsMutation.isPending ? (
                  <>
                    <Spinner />
                    {t("pages.ai_parser.add_columns_dialog.saving")}
                  </>
                ) : (
                  t("pages.ai_parser.add_columns_dialog.save")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

const getDataTypeDescription = (dataType: string, t: TFunction) => {
  switch (dataType) {
    case "string":
      return ""
    case "number":
      return t("pages.ai_parser.add_columns_dialog.data_type_descriptions.number")
    case "boolean":
      return t("pages.ai_parser.add_columns_dialog.data_type_descriptions.boolean")
    case "date":
      return t("pages.ai_parser.add_columns_dialog.data_type_descriptions.date")
    case "options":
      return t("pages.ai_parser.add_columns_dialog.data_type_descriptions.options")
    default:
      return ""
  }
}
